import { pathOr } from 'ramda';

import { PAGE_REQUEST } from 'store/app/types';

import { types } from './actions';
import { CHANNEL_SETTINGS_MODAL_TABS } from './constants';

const initialState = {
  activeTab: CHANNEL_SETTINGS_MODAL_TABS.SETTINGS,
  areSharesCreating: false,
  channelId: null,
  isChannelSettingsModalOpen: false,
  isChannelSettingsSaving: false,
  isCreatingChannel: false,
  isFetchingData: false,
  isNewChannelModalOpen: false,
  isRevokingAccessToChannel: false,
  isTrustedThirdPartyChannelSettingsModalOpen: false,
  shareExpirationChanges: {},
  technicalDataPackage: null,
};

const reducer = (state = initialState, action) => {
  const { type, payload } = action;

  switch (type) {
    case PAGE_REQUEST:
      return {
        ...initialState,
      };
    case types.CHANNEL_SETTINGS_CHANGE_EXPIRATION:
      return {
        ...state,
        shareExpirationChanges: {
          ...state.shareExpirationChanges,
          [payload.shareId]: payload.expirationDate,
        },
      };
    case types.CHANNEL_SETTINGS_MODAL_CLOSE:
      return {
        ...state,
        isChannelSettingsModalOpen: false,
      };
    case types.CHANNEL_SETTINGS_MODAL_OPEN:
      return {
        ...state,
        activeTab: payload.tab
          ? payload.tab
          : CHANNEL_SETTINGS_MODAL_TABS.SETTINGS,
        channelId: payload.channelId,
        isChannelSettingsModalOpen: true,
        isFetchingData: true,
        shareExpirationChanges: {},
      };
    case types.CHANNEL_SETTINGS_MODAL_SET_TAB:
      return {
        ...state,
        activeTab: payload.tabKey,
      };
    case types.CREATE_CHANNEL_ERROR:
      return {
        ...state,
        isCreatingChannel: false,
      };
    case types.CREATE_CHANNEL_REQUEST:
      return {
        ...state,
        isCreatingChannel: true,
      };
    case types.CREATE_CHANNEL_SUCCESS:
      return {
        ...state,
        isCreatingChannel: false,
        isNewChannelModalOpen: false,
      };
    case types.CREATE_SHARES_ERROR:
      return {
        ...state,
        areSharesCreating: false,
      };
    case types.CREATE_SHARES_REQUEST:
      return {
        ...state,
        areSharesCreating: true,
      };
    case types.CREATE_SHARES_SUCCESS:
      return {
        ...state,
        activeTab: CHANNEL_SETTINGS_MODAL_TABS.SETTINGS,
        areSharesCreating: false,
      };
    case types.DELETE_SHARES_FOR_CHANNEL_ERROR:
      return {
        ...state,
        isRevokingAccessToChannel: false,
      };
    case types.DELETE_SHARES_FOR_CHANNEL_REQUEST:
      return {
        ...state,
        isRevokingAccessToChannel: true,
      };
    case types.DELETE_SHARES_FOR_CHANNEL_SUCCESS:
      return {
        ...state,
        isChannelSettingsModalOpen: false,
        isMscChannelSettingsModalOpen: false,
        isRevokingAccessToChannel: false,
      };
    case types.INIT_SHARE_SETTINGS_ERROR:
      return {
        ...initialState,
      };
    case types.INIT_SHARE_SETTINGS_SUCCESS:
      return {
        ...state,
        isFetchingData: false,
        technicalDataPackage: payload.technicalDataPackage,
      };
    case types.NEW_CHANNEL_MODAL_CLOSE:
      return {
        ...state,
        isNewChannelModalOpen: false,
      };
    case types.NEW_CHANNEL_MODAL_OPEN:
      return {
        ...state,
        isFetchingData: true,
        isNewChannelModalOpen: true,
        technicalDataPackage: {},
      };
    case types.TRUSTED_THIRD_PARTY_CHANNEL_SETTINGS_MODAL_CLOSE:
      return {
        ...state,
        isTrustedThirdPartyChannelSettingsModalOpen: false,
      };
    case types.TRUSTED_THIRD_PARTY_CHANNEL_SETTINGS_MODAL_OPEN:
      return {
        ...state,
        channelId: payload.channelId,
        isFetchingData: true,
        isTrustedThirdPartyChannelSettingsModalOpen: true,
      };
    case types.UPDATE_CHANNEL_SETTINGS_ERROR:
      return {
        ...state,
        isChannelSettingsSaving: false,
      };
    case types.UPDATE_CHANNEL_SETTINGS_REQUEST:
      return {
        ...state,
        isChannelSettingsSaving: true,
      };
    case types.UPDATE_CHANNEL_SETTINGS_SUCCESS:
      return {
        ...state,
        isChannelSettingsSaving: false,
      };
    default:
      return state;
  }
};

export default reducer;

const getScope = (state) => state.modules.common.chat.shares;

export const areSharesCreating = (state) => getScope(state).areSharesCreating;

export const getActiveTab = (state) => getScope(state).activeTab;

export const getTechnicalDataPackage = (state) =>
  getScope(state).technicalDataPackage;

/**
 * Gather primary files for display in share settings. Only want to show the
 * primary files that are not associated with an initial assembly upload
 * @param state
 * @returns {*[]}
 */
export const getPrimaryFiles = (state) => {
  const partInformation = pathOr(
    [],
    ['partInformation'],
    getTechnicalDataPackage(state),
  );
  const primaryFiles = [];
  for (const part of partInformation) {
    if (!part.isManual) {
      const primaryFile = part.partFiles.find((pf) => pf.isPrimaryFile);
      if (
        primaryFile &&
        (part.isRootPart || !primaryFile.hasAssemblyParentFile)
      ) {
        primaryFiles.push(primaryFile);
      }
    }
  }
  return primaryFiles;
};

export const getChannelId = (state) => getScope(state).channelId;

export const getSupportingFiles = (state) => {
  const partInformation = pathOr(
    [],
    ['partInformation'],
    getTechnicalDataPackage(state),
  );
  let supportingFiles = [];
  for (const part of partInformation) {
    supportingFiles = supportingFiles.concat(
      part.partFiles.filter((partFile) => !partFile.isPrimaryFile),
    );
  }
  return supportingFiles;
};

export const isChannelSettingsModalOpen = (state) =>
  getScope(state).isChannelSettingsModalOpen;

export const isChannelSettingsSaving = (state) =>
  getScope(state).isChannelSettingsSaving;

export const isCreatingChannel = (state) => getScope(state).isCreatingChannel;

export const isFetchingData = (state) => getScope(state).isFetchingData;

export const isTrustedThirdPartyChannelSettingsModalOpen = (state) =>
  getScope(state).isTrustedThirdPartyChannelSettingsModalOpen;

export const isNewChannelModalOpen = (state) =>
  getScope(state).isNewChannelModalOpen;

export const isRevokingAccessToChannel = (state) =>
  getScope(state).isRevokingAccessToChannel;

export const getShareExpirationChanges = (state) =>
  getScope(state).shareExpirationChanges;
